/**
 * HARDLOCK v18.12.71-STABLE-HYDRATION-FIX
 * Archivo critico de Planeacion/Seguimiento.
 * No modificar sin snapshot, pruebas hardlock, localhost validation y revision de no regresion.
 */
import { 
    IGNORED_KEYS, SECTION_FIELDS, SAFE_RUNTIME_KEYS, 
    INVALID_PLACEHOLDERS, PLANNING_SECTION_CONTRACT 
} from './planningConstants';
import { SectionNumber } from './planningTypes';

export const normalizeStr = (s: any) => {
    if (s === null || s === undefined) return "";
    return String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();
};

export const isMeaningfulValue = (v: any): boolean => {
    if (v === null || v === undefined) return false;
    const s = String(v).trim();
    if (s === "" || s === "0" || s === "0%") return false;
    
    const normalized = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    
    // v18.12.41.1-COUNTING-RESTORE-STABLE: Restaurar validación exacta
    if (INVALID_PLACEHOLDERS.includes(normalized)) return false;
    
    // Ignorar numéricos tipo "1.", "2)"
    if (/^\d[\.\)]?$/.test(s)) return false;

    return s.length > 0;
};

/**
 * v18.12.41: Deep Freeze para proteger estructuras contra mutación runtime.
 */
export const deepFreeze = (obj: any) => {
    if (obj === null || typeof obj !== 'object') return obj;
    Object.freeze(obj);
    Object.getOwnPropertyNames(obj).forEach((prop) => {
        if (obj.hasOwnProperty(prop) &&
            obj[prop] !== null &&
            (typeof obj[prop] === "object" || typeof obj[prop] === "function") &&
            !Object.isFrozen(obj[prop])) {
            deepFreeze(obj[prop]);
        }
    });
    return obj;
};

/**
 * v18.12.41: Resolución segura de número de sección (1-9).
 * Evita coerción silenciosa y valores inválidos.
 */
export const resolveSectionNumberSafe = (val: any): SectionNumber | null => {
    if (val === null || val === undefined) return null;
    
    // Si es un UUID (más de 8 caracteres y contiene guiones o letras), no es un número de sección
    if (typeof val === 'string' && (val.length > 8 || /[a-zA-Z]/.test(val))) {
        // Excepción: si empieza con 'S' y es seguido de un solo dígito (S1-S9)
        if (/^S[1-9]$/i.test(val)) {
            return parseInt(val.substring(1), 10) as SectionNumber;
        }
        return null;
    }

    let num: number;
    if (typeof val === 'number') {
        num = val;
    } else if (typeof val === 'string') {
        num = parseInt(val, 10);
    } else {
        return null;
    }
    
    if (isNaN(num) || num < 1 || num > 9) return null;
    return num as SectionNumber;
};

/**
 * v18.12.65: Validación ESTRICTA de contrato con Detección de DRIFT y Protección de Integridad.
 */
export const validateRuntimeContract = (section: any, resolvedSn: number | null): { isValid: boolean; error?: string } => {
    if (!section || !section.id) return { isValid: false, error: "Missing Section ID" };
    
    // 1. Detección de DRIFT
    const idNum = resolveSectionNumberSafe(section.id);
    const sn = resolveSectionNumberSafe(resolvedSn);
    
    if (idNum !== null && sn !== null && idNum !== sn) {
        return { isValid: false, error: `Structural Drift Detected: ID ${section.id} mismatch with S${sn}` };
    }

    if (!sn) return { isValid: false, error: "Invalid or Missing Section Number" };
    
    const contract = PLANNING_SECTION_CONTRACT[sn];
    if (!contract) return { isValid: false, error: `Critical: No canonical contract defined for S${sn}` };

    // 2. Validación de Tipo Estructural
    if (!contract.type || !contract.canonicalKey) {
        return { isValid: false, error: `Incomplete contract metadata for S${sn}` };
    }

    // 3. Payload Integrity Check
    try {
        const content = typeof section.content === 'string' ? JSON.parse(section.content) : (section.content || {});
        // Verificamos si existe alguna data mínima o si es una estructura válida
        if (typeof content !== 'object') {
            return { isValid: false, error: "Payload content is not a valid object" };
        }
    } catch (e) {
        return { isValid: false, error: "Malformed JSON Payload" };
    }

    return { isValid: true };
};

/**
 * v18.12.40.2: Conteo de campos significativos.
 * Prioriza SECTION_FIELDS pero permite columnas dinámicas.
 */
export const filledFieldCount = (row: any, sn?: number): number => {
    if (!row || typeof row !== 'object') return 0;
    const allowed = sn ? SECTION_FIELDS[sn] : null;
    
    return Object.entries(row).filter(([k, v]) => {
        // Ignorar siempre keys de sistema/runtime
        if (IGNORED_KEYS.includes(k)) return false;
        if (SAFE_RUNTIME_KEYS.includes(k)) return false;
        if (k.startsWith('_config_')) return false;
        
        // v18.12.52: El conteo ahora es 'Omnívoro'. 
        // Si el campo está en el contrato, se valida. 
        // Si es una columna dinámica (no en el contrato), también cuenta si tiene valor significativo.
        if (allowed && allowed.includes(k)) {
            return isMeaningfulValue(v);
        }
        
        return isMeaningfulValue(v);
    }).length;
};

export const rowHasAtLeast = (row: any, n: number, sn?: number): boolean => {
    return filledFieldCount(row, sn) >= n;
};
